// src/utils/transformationUtils.js

/**
 * Applies the transformation rules to the given input value.
 *
 * @param {string} inputValue - The value to transform.
 * @param {Object} transformations - An object containing transformation settings.
 *    Expected properties:
 *      - capitalize: "all-caps", "no-caps", "first-letter", or "none"
 *      - spacing: "underscore", "hyphen", "no-spacing", or "none"
 *      - allowSymbols: boolean (if false, non-alphanumeric characters except spaces and hyphens are removed)
 *      - encode: boolean (reserved for submission encoding if needed)
 * @param {boolean} [isSubmission=false] - Flag indicating whether this transformation is for submission.
 * @returns {string} - The transformed value.
 */
export function applyTransformations(inputValue, transformations, isSubmission = false) {
  // If there are no transformation settings or the input is null/undefined, return the original input.
  if (!transformations || inputValue === null || inputValue === undefined) {
    return inputValue;
  }

  // Convert inputValue to a string.
  let transformedValue = inputValue.toString();

  // If the input is only whitespace, return an empty string.
  if (transformedValue.trim() === "") {
    return "";
  }

  // Apply capitalization transformation.
  switch (transformations.capitalize) {
    case 'all-caps':
      transformedValue = transformedValue.toUpperCase();
      break;
    case 'no-caps':
      transformedValue = transformedValue.toLowerCase();
      break;
    case 'first-letter':
      transformedValue = transformedValue.charAt(0).toUpperCase() + transformedValue.slice(1).toLowerCase();
      break;
    case 'none': // "No restrictions" for capitalization.
    default:
      break;
  }

  // Apply spacing transformation.
  switch (transformations.spacing) {
    case 'underscore':
      transformedValue = transformedValue.replace(/\s+/g, '_');
      break;
    case 'hyphen':
      transformedValue = transformedValue.replace(/\s+/g, '-');
      break;
    case 'no-spacing':
      transformedValue = transformedValue.replace(/\s+/g, '');
      break;
    case 'none': // "No restrictions" for spacing.
    default:
      break;
  }

  // Apply symbol restriction: if allowSymbols is false, remove non-alphanumeric characters (except spaces and hyphens).
  if (transformations.allowSymbols === false) {
    transformedValue = transformedValue.replace(/[^\w\s-]/gi, '');
  }

  // (Optional) Apply encoding logic on submission if needed.
  // if (isSubmission && transformations.encode) {
  //   // Add encoding logic here.
  // }

  return transformedValue;
}
